import { createPortal } from 'react-dom'
import type { DuplicateRoleGroup, RoleOccurrence, SimilarRolePair } from '@shared/roster-validation'

interface Props {
  fileName: string
  duplicateRoles: DuplicateRoleGroup[]
  similarRoles: SimilarRolePair[]
  onContinue: () => void
  onCancel: () => void
}

function describeLocation(sheet: string | undefined, row: number): string {
  return sheet ? `sheet "${sheet}", row ${row}` : `row ${row}`
}

function describeOccurrence(occurrence: RoleOccurrence): string {
  const where = describeLocation(occurrence.sheet, occurrence.row)
  return occurrence.ftes === undefined ? where : `${where} (${occurrence.ftes} FTE)`
}

export default function RosterQualityModal({ fileName, duplicateRoles, similarRoles, onContinue, onCancel }: Props) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="roster-quality-title"
        className="bg-white rounded-2xl shadow-2xl w-full mx-4 flex flex-col"
        style={{ maxWidth: 600, maxHeight: '90vh' }}
      >
        <div className="px-6 pt-5 pb-3 border-b border-gray-100">
          <h2 id="roster-quality-title" className="text-base font-bold text-gray-900">Review roster before analysis</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {fileName}. Nothing has been changed. Review the items below, then continue or go back and fix the file.
          </p>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-5">
          {duplicateRoles.length > 0 && (
            <section aria-label="Duplicate roles">
              <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-1">
                Duplicate roles ({duplicateRoles.length})
              </p>
              <p className="text-xs text-gray-500 mb-2 leading-relaxed">
                The same subfunction and role appears more than once. Headcount handling is unchanged. If your roster has separate Function and Subfunction columns, only one of them is used, so roles in different functions that share a subfunction name can appear here.
              </p>
              <ul className="space-y-2">
                {duplicateRoles.map((group, i) => (
                  <li key={i} className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                    <span className="font-semibold">{group.role}</span> in {group.subfunction}
                    {group.crossSheet ? ' (across sheets)' : ''}
                    <ul className="mt-1 pl-3 list-disc list-outside ml-2">
                      {group.occurrences.map((occurrence, j) => (
                        <li key={j}>{describeOccurrence(occurrence)}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {similarRoles.length > 0 && (
            <section aria-label="Similar role names">
              <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-1">
                Similar role names ({similarRoles.length})
              </p>
              <p className="text-xs text-gray-500 mb-2 leading-relaxed">
                Recommendation only. These names are close enough that they may be the same role spelled two ways. They are kept as separate roles.
              </p>
              <ul className="space-y-2">
                {similarRoles.map((pair, i) => (
                  <li key={i} className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900">
                    <span className="font-semibold">{pair.roleA}</span> ({describeLocation(pair.locationA.sheet, pair.locationA.row)})
                    {' and '}
                    <span className="font-semibold">{pair.roleB}</span> ({describeLocation(pair.locationB.sheet, pair.locationB.row)})
                    {' in '}{pair.subfunction}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex flex-row gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2.5 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="flex-1 py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
          >
            Acknowledge and continue
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
